class VideoCarousel extends HTMLElement {
  #carousel = null;
  #tiles = null;
  #unmuteButtons = null;
  #playButtons = null;
  #progressFill = null;
  #popup = null;
  #popupVideo = null;
  #popupPlayPause = null;
  #popupMute = null;
  #popupPrev = null;
  #popupNext = null;
  #popupClose = null;
  #popupIndex = 0;
  #isUpdating = false;
  #blocksCount = 0;
  #tileWidth = 300;
  #gap = 0;
  #tileWithGap = 0;
  #middleIndex = 0;
  #scrollTimeout = null;
  #rafId = null;
  #dragStartX = null;
  #dragStartY = null;
  #isDragging = false;
  #dragThreshold = 30;
  #initialized = false;
  #mobileMediaQuery = null;
  #autoplayCenter = false;
  #showPopup = false;
  #playLabel = 'Play video';
  #pauseLabel = 'Pause video';
  #muteLabel = 'Mute video';
  #unmuteLabel = 'Unmute video';

  static get observedAttributes() {
    return ['data-carousel-id', 'data-blocks-count', 'data-gap', 'data-middle-index'];
  }

  connectedCallback() {
    this.#mobileMediaQuery = window.matchMedia('(max-width: 699px)');

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.#initWhenReady();
      });
    } else {
      this.#initWhenReady();
    }

    this.#mobileMediaQuery.addEventListener('change', () => {
      if (this.#initialized) {
        this.#onBreakpointChange();
      }
    });
  }

  disconnectedCallback() {
    this.#cleanup();
  }

  #isMobile() {
    return this.#mobileMediaQuery?.matches ?? false;
  }

  #initWhenReady() {
    if (this.#initialized) return;

    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        this.#initialize();
      }, { timeout: 2000 });
    } else {
      setTimeout(() => {
        this.#initialize();
      }, 100);
    }
  }

  #initialize() {
    if (this.#initialized) return;

    const carouselId = this.dataset.carouselId;
    if (!carouselId) return;

    this.#carousel = document.getElementById(carouselId);
    if (!this.#carousel) return;

    this.#tiles = this.querySelectorAll('.video-carousel__tile');
    this.#unmuteButtons = this.querySelectorAll('.video-carousel__unmute-button');
    this.#playButtons = this.querySelectorAll('.video-carousel__play-button');
    this.#progressFill = this.querySelector('.video-carousel__progress-fill');

    this.#popup = this.querySelector('.video-carousel__popup');
    this.#popupVideo = this.querySelector('.video-carousel__popup-video');
    this.#popupPlayPause = this.querySelector('.video-carousel__popup-play-pause');
    this.#popupMute = this.querySelector('.video-carousel__popup-mute');
    this.#popupPrev = this.querySelector('.video-carousel__popup-prev');
    this.#popupNext = this.querySelector('.video-carousel__popup-next');
    this.#popupClose = this.querySelector('.video-carousel__popup-close');

    this.#blocksCount = parseInt(this.dataset.blocksCount) || 0;
    this.#gap = parseInt(this.dataset.gap) || 0;
    this.#tileWithGap = this.#tileWidth + this.#gap;
    this.#middleIndex = parseInt(this.dataset.middleIndex) || 0;
    this.#autoplayCenter = this.hasAttribute('data-autoplay-center');
    this.#showPopup = this.hasAttribute('data-show-popup');
    this.#playLabel = this.dataset.playLabel || 'Play video';
    this.#pauseLabel = this.dataset.pauseLabel || 'Pause video';
    this.#muteLabel = this.dataset.muteLabel || 'Mute video';
    this.#unmuteLabel = this.dataset.unmuteLabel || 'Unmute video';

    if (this.#popup) {
      document.body.appendChild(this.#popup);
    }

    this.#attachEventListeners();
    this.#initializeCarousel();

    this.#initialized = true;
  }

  #cleanup() {
    if (this.#scrollTimeout) {
      clearTimeout(this.#scrollTimeout);
      this.#scrollTimeout = null;
    }
    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
  }

  #onBreakpointChange() {
    this.#closePopup();
    this.#pauseAllVideos({ resetFacade: true });
    this.#tiles.forEach((tile) => {
      tile.classList.remove('video-carousel__tile--center');
    });

    if (this.#isMobile()) {
      this.#carousel.scrollTo({ left: 0, behavior: 'auto' });
      this.#updateProgress();
    } else {
      const startPos = this.#calculateStartPosition();
      this.#carousel.scrollTo({
        left: Math.max(0, startPos),
        behavior: 'auto'
      });
      setTimeout(() => this.#updateCenterVideo(), 100);
    }
  }

  #pauseAllVideos({ resetFacade = false } = {}) {
    this.#tiles.forEach((tile) => {
      const video = tile.querySelector('video');
      if (video) {
        video.pause();
        if (resetFacade) {
          video.currentTime = 0;
        }
        video.muted = true;
        delete tile.dataset.userUnmuted;
      }
      const videoMedia = tile.querySelector('video-media');
      if (videoMedia) {
        videoMedia.removeAttribute('playing');
        if (resetFacade) {
          videoMedia.removeAttribute('loaded');
        }
      }
      this.#setTilePlaying(tile, false);
    });
  }

  #calculateStartPosition() {
    const middleTile = this.#tiles[this.#middleIndex];
    if (!middleTile) return 0;
    return middleTile.offsetLeft - (this.#carousel.clientWidth / 2) + (middleTile.offsetWidth / 2);
  }

  #getOriginalIndex(tile) {
    return parseInt(tile.dataset.videoIndex);
  }

  #findCenterTile() {
    const carouselRect = this.#carousel.getBoundingClientRect();
    const carouselCenter = carouselRect.left + carouselRect.width / 2;

    let closestTile = null;
    let closestDistance = Infinity;

    this.#tiles.forEach((tile) => {
      const tileRect = tile.getBoundingClientRect();
      const tileCenter = tileRect.left + tileRect.width / 2;
      const distance = Math.abs(tileCenter - carouselCenter);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestTile = tile;
      }
    });

    return closestTile;
  }

  #setTilePlaying(tile, isPlaying) {
    tile.classList.toggle('video-carousel__tile--playing', isPlaying);
    this.#updatePlayButtonState(tile, isPlaying);
    if (isPlaying) {
      this.#syncMuteButton(tile);
    }
  }

  #syncMuteButton(tile) {
    const button = tile.querySelector('.video-carousel__unmute-button');
    const video = tile.querySelector('video');
    if (!button || !video) return;
    this.#updateUnmuteButtonIcon(button, video.muted);
  }

  #getVideoSrc(tile) {
    const video = tile.querySelector('video');
    if (video) {
      const source = video.querySelector('source');
      if (source?.getAttribute('src')) return source.getAttribute('src');
      if (video.currentSrc) return video.currentSrc;
      if (video.src) return video.src;
    }
    return tile.dataset.videoSrc || '';
  }

  #updatePlayButtonState(tile, isPlaying) {
    const button = tile.querySelector('.video-carousel__play-button:not(:disabled)');
    if (!button) return;

    const playIcon = button.querySelector('.video-carousel__play-icon-wrap--play');
    const pauseIcon = button.querySelector('.video-carousel__play-icon-wrap--pause');

    button.dataset.state = isPlaying ? 'pause' : 'play';
    button.setAttribute('aria-label', isPlaying ? this.#pauseLabel : this.#playLabel);

    if (playIcon) playIcon.hidden = isPlaying;
    if (pauseIcon) pauseIcon.hidden = !isPlaying;
  }

  #updatePopupPlayButton(isPlaying) {
    if (!this.#popupPlayPause) return;

    const playIcon = this.#popupPlayPause.querySelector('.video-carousel__popup-icon--play');
    const pauseIcon = this.#popupPlayPause.querySelector('.video-carousel__popup-icon--pause');

    this.#popupPlayPause.dataset.state = isPlaying ? 'pause' : 'play';
    this.#popupPlayPause.setAttribute('aria-label', isPlaying ? this.#pauseLabel : this.#playLabel);

    if (playIcon) playIcon.hidden = isPlaying;
    if (pauseIcon) pauseIcon.hidden = !isPlaying;
  }

  #updateCenterVideo() {
    if (this.#isMobile() || this.#isUpdating) return;
    if (!this.#autoplayCenter) return;

    this.#isUpdating = true;

    const centerTile = this.#findCenterTile();
    if (!centerTile) {
      setTimeout(() => { this.#isUpdating = false; }, 100);
      return;
    }

    requestAnimationFrame(() => {
      this.#tiles.forEach((tile) => {
        const videoMedia = tile.querySelector('video-media');
        const video = videoMedia?.querySelector('video');
        const isCenter = tile === centerTile;

        if (isCenter) {
          tile.classList.add('video-carousel__tile--center');
          this.#setTilePlaying(tile, true);
          if (videoMedia && video) {
            if (!tile.dataset.userUnmuted) {
              video.muted = true;
            }
            video.loop = true;
            if (video.paused) {
              video.play().catch(() => {});
            }
          }
        } else {
          tile.classList.remove('video-carousel__tile--center');
          this.#setTilePlaying(tile, false);
          if (videoMedia && video) {
            video.muted = true;
            video.pause();
            video.currentTime = 0;
            delete tile.dataset.userUnmuted;
          }
        }
      });

      setTimeout(() => { this.#isUpdating = false; }, 100);
    });
  }

  #getCurrentCenterIndex() {
    const centerTile = this.#findCenterTile();
    if (!centerTile) return this.#middleIndex;
    return this.#getOriginalIndex(centerTile);
  }

  #navigateToIndex(targetIndex) {
    if (targetIndex < 0 || targetIndex >= this.#blocksCount) return;

    const targetTile = Array.from(this.#tiles).find(t =>
      parseInt(t.dataset.videoIndex) === targetIndex
    );
    if (!targetTile) return;

    if (this.#isMobile()) {
      this.#carousel.scrollTo({
        left: Math.max(0, targetTile.offsetLeft - this.#carousel.offsetLeft),
        behavior: 'smooth'
      });
      return;
    }

    const targetScroll = targetTile.offsetLeft - (this.#carousel.clientWidth / 2) + (targetTile.offsetWidth / 2);

    this.#carousel.scrollTo({
      left: Math.max(0, Math.min(targetScroll, this.#carousel.scrollWidth - this.#carousel.clientWidth)),
      behavior: 'smooth'
    });
  }

  #updateProgress() {
    if (!this.#progressFill || !this.#carousel) return;

    const max = this.#carousel.scrollWidth - this.#carousel.clientWidth;
    const ratio = max > 0 ? this.#carousel.scrollLeft / max : 0;
    this.#progressFill.style.width = `${20 + ratio * 80}%`;
  }

  #updateUnmuteButtonIcon(button, isMuted) {
    const existingSvg = button.querySelector('svg');
    if (!existingSvg) return;

    const muteIconSvg = '<svg aria-hidden="true" focusable="false" fill="none" width="20" class="icon icon-mute" viewBox="0 0 25 24"><path d="M5.335 16.366H1V8.42h4.335m0 7.947V8.42m0 7.947 7.587 5.42V3L5.335 8.419" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M20.287 21.083A11.98 11.98 0 0 0 24 12.393c0-3.422-1.425-6.493-3.713-8.69m-3.582 3.582a6.947 6.947 0 0 1 0 10.215" stroke="currentColor" stroke-width="1.5"/></svg>';
    const unmuteIconSvg = '<svg aria-hidden="true" focusable="false" fill="none" width="20" class="icon icon-unmute" viewBox="0 0 25 24"><path d="M5.335 16.366H1V8.42h4.335m0 7.947V8.42m0 7.947 1.204.86M5.335 8.42 12.922 3v8M9 18.984l3.921 2.801V15.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M20.287 21.083A11.98 11.98 0 0 0 24 12.393c0-3.422-1.425-6.493-3.713-8.69m-3.582 3.582a6.947 6.947 0 0 1 0 10.215" stroke="currentColor" stroke-width="1.5"/><path d="m22.5 1.5-21 21" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    existingSvg.outerHTML = isMuted ? unmuteIconSvg : muteIconSvg;
    button.setAttribute('aria-label', isMuted ? this.#unmuteLabel : this.#muteLabel);
    button.dataset.muted = isMuted ? 'true' : 'false';
  }

  #getTileByIndex(index) {
    return Array.from(this.#tiles).find(t => parseInt(t.dataset.videoIndex) === index);
  }

  #pauseTileVideo(tile) {
    const videoMedia = tile.querySelector('video-media');
    const video = videoMedia?.querySelector('video');
    if (!video) return;

    video.pause();
    this.#setTilePlaying(tile, false);
  }

  #playTileVideo(tile, { withSound = true } = {}) {
    const videoMedia = tile.querySelector('video-media');
    const video = videoMedia?.querySelector('video');
    if (!videoMedia || !video) return;

    this.#tiles.forEach((otherTile) => {
      if (otherTile === tile) return;
      this.#pauseTileVideo(otherTile);
      const otherVideo = otherTile.querySelector('video');
      if (otherVideo) {
        otherVideo.muted = true;
        delete otherTile.dataset.userUnmuted;
      }
    });

    if (!videoMedia.hasAttribute('loaded')) {
      videoMedia.click();
    }

    if (withSound) {
      video.muted = false;
      tile.dataset.userUnmuted = 'true';
    }

    video.loop = false;

    video.play().then(() => {
      this.#setTilePlaying(tile, true);
      this.#syncMuteButton(tile);
    }).catch(() => {});

    video.addEventListener('ended', () => {
      this.#setTilePlaying(tile, false);
      if (!tile.dataset.userUnmuted) {
        video.muted = true;
      }
    }, { once: true });
  }

  #toggleTilePlayback(tile) {
    const video = tile.querySelector('video');
    if (!video) return;

    if (this.#showPopup && this.#popup) {
      const index = this.#getOriginalIndex(tile);

      if (!this.#popup.hasAttribute('hidden') && this.#popupIndex === index) {
        this.#togglePopupPlayback();
        return;
      }

      this.#openPopup(index);
      return;
    }

    if (!video.paused && tile.classList.contains('video-carousel__tile--playing')) {
      this.#pauseTileVideo(tile);
      return;
    }

    this.#playTileVideo(tile, { withSound: true });
  }

  #openPopup(index) {
    if (!this.#popup || !this.#popupVideo) return;

    const tile = this.#getTileByIndex(index);
    if (!tile) return;

    const src = this.#getVideoSrc(tile);
    if (!src) return;

    this.#popupIndex = index;
    this.#pauseAllVideos({ resetFacade: false });

    this.#popup.removeAttribute('hidden');
    this.#popup.setAttribute('aria-hidden', 'false');

    const poster = tile.dataset.videoPoster || '';
    if (this.#popupVideo.getAttribute('src') !== src) {
      this.#popupVideo.setAttribute('src', src);
      this.#popupVideo.poster = poster;
      this.#popupVideo.load();
    } else {
      this.#popupVideo.currentTime = 0;
    }

    this.#popupVideo.muted = false;
    if (this.#popupMute) {
      this.#popupMute.dataset.muted = 'false';
      this.#updateUnmuteButtonIcon(this.#popupMute, false);
    }

    const playPromise = this.#popupVideo.play();
    if (playPromise) {
      playPromise.then(() => {
        this.#updatePopupPlayButton(true);
      }).catch(() => {
        this.#popupVideo.muted = true;
        if (this.#popupMute) {
          this.#popupMute.dataset.muted = 'true';
          this.#updateUnmuteButtonIcon(this.#popupMute, true);
        }
        this.#popupVideo.play().catch(() => {});
      });
    }

    this.#updatePopupNavState();
    this.#setTilePlaying(tile, true);
  }

  #closePopup() {
    if (!this.#popup || !this.#popupVideo) return;

    this.#popupVideo.pause();
    this.#popup.setAttribute('hidden', '');
    this.#popup.setAttribute('aria-hidden', 'true');
    this.#updatePopupPlayButton(false);

    this.#tiles.forEach((tile) => {
      this.#setTilePlaying(tile, false);
    });
  }

  #popupGoTo(index) {
    if (index < 0 || index >= this.#blocksCount) return;
    this.#openPopup(index);
    this.#navigateToIndex(index);
  }

  #updatePopupNavState() {
    if (!this.#popupPrev || !this.#popupNext) return;
    this.#popupPrev.disabled = this.#popupIndex <= 0;
    this.#popupNext.disabled = this.#popupIndex >= this.#blocksCount - 1;
  }

  #togglePopupPlayback() {
    if (!this.#popupVideo) return;

    if (this.#popupVideo.paused) {
      this.#popupVideo.play().then(() => {
        this.#updatePopupPlayButton(true);
      }).catch(() => {});
    } else {
      this.#popupVideo.pause();
      this.#updatePopupPlayButton(false);
    }
  }

  #attachEventListeners() {
    this.#carousel.addEventListener('scroll', this.#handleScroll.bind(this), { passive: true });
    this.#carousel.addEventListener('carousel:settle', () => {
      if (!this.#isMobile()) {
        this.#updateCenterVideo();
      }
    });

    this.#carousel.addEventListener('mousedown', this.#handleMouseDown.bind(this));
    this.#carousel.addEventListener('mousemove', this.#handleMouseMove.bind(this));
    this.#carousel.addEventListener('mouseup', this.#handleMouseUp.bind(this));
    this.#carousel.addEventListener('mouseleave', this.#handleMouseLeave.bind(this));

    this.#playButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (button.disabled) return;

        const tile = button.closest('.video-carousel__tile');
        if (tile) {
          this.#toggleTilePlayback(tile);
        }
      });
    });

    this.#tiles.forEach((tile) => {
      const originalIndex = this.#getOriginalIndex(tile);
      const videoMedia = tile.querySelector('video-media');
      const video = videoMedia?.querySelector('video');

      if (video) {
        video.addEventListener('click', (e) => {
          if (this.#isMobile()) return;
          e.preventDefault();
          e.stopPropagation();
          this.#handleTileClick(tile, originalIndex);
        });

        video.addEventListener('play', () => {
          this.#setTilePlaying(tile, true);
        });

        video.addEventListener('pause', () => {
          if (video.paused) {
            this.#setTilePlaying(tile, false);
          }
        });
      }

      tile.addEventListener('click', (e) => {
        if (this.#isMobile()) return;
        if (e.target.closest('.video-carousel__unmute-button') ||
            e.target.closest('.video-carousel__play-button') ||
            e.target.closest('.video-carousel__product-content') ||
            e.target.closest('.video-carousel__tile-link') ||
            e.target.closest('a')) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        this.#handleTileClick(tile, originalIndex);
      });
    });

    this.#unmuteButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
        const tile = button.closest('.video-carousel__tile');
        const videoMedia = tile?.querySelector('video-media');
        const video = videoMedia?.querySelector('video');

        if (video) {
          video.muted = !video.muted;
          if (!video.muted) {
            tile.dataset.userUnmuted = 'true';
          } else {
            delete tile.dataset.userUnmuted;
          }
          this.#updateUnmuteButtonIcon(button, video.muted);
        }
      });
    });

    this.#tiles.forEach((tile) => {
      const productTile = tile.querySelector('.video-carousel__product-tile');
      if (productTile) {
        productTile.addEventListener('click', (e) => {
          const quickBuyId = productTile.dataset.quickBuyId;
          if (quickBuyId) {
            const quickBuyModal = document.getElementById(quickBuyId);
            if (quickBuyModal && typeof quickBuyModal.show === 'function') {
              e.preventDefault();
              e.stopPropagation();
              quickBuyModal.show();
            }
          }
        });
      }
    });

    if (this.#popupClose) {
      this.#popupClose.addEventListener('click', () => this.#closePopup());
    }

    if (this.#popupPlayPause) {
      this.#popupPlayPause.addEventListener('click', () => this.#togglePopupPlayback());
    }

    if (this.#popupPrev) {
      this.#popupPrev.addEventListener('click', () => this.#popupGoTo(this.#popupIndex - 1));
    }

    if (this.#popupNext) {
      this.#popupNext.addEventListener('click', () => this.#popupGoTo(this.#popupIndex + 1));
    }

    if (this.#popupMute) {
      this.#popupMute.addEventListener('click', () => {
        if (!this.#popupVideo) return;
        this.#popupVideo.muted = !this.#popupVideo.muted;
        this.#popupMute.dataset.muted = this.#popupVideo.muted ? 'true' : 'false';
        this.#updateUnmuteButtonIcon(this.#popupMute, this.#popupVideo.muted);
      });
    }

    if (this.#popupVideo) {
      this.#popupVideo.addEventListener('ended', () => {
        this.#updatePopupPlayButton(false);
        if (this.#popupIndex < this.#blocksCount - 1) {
          this.#popupGoTo(this.#popupIndex + 1);
        }
      });

      this.#popupVideo.addEventListener('play', () => {
        this.#updatePopupPlayButton(true);
        const tile = this.#getTileByIndex(this.#popupIndex);
        if (tile) this.#setTilePlaying(tile, true);
      });

      this.#popupVideo.addEventListener('pause', () => {
        if (this.#popupVideo.paused) {
          this.#updatePopupPlayButton(false);
          const tile = this.#getTileByIndex(this.#popupIndex);
          if (tile) this.#setTilePlaying(tile, false);
        }
      });
    }
  }

  #handleScroll() {
    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
    }

    this.#rafId = requestAnimationFrame(() => {
      if (this.#isMobile()) {
        this.#updateProgress();
      } else {
        const centerTile = this.#findCenterTile();
        if (centerTile) {
          this.#tiles.forEach((tile) => {
            if (tile === centerTile) {
              tile.classList.add('video-carousel__tile--center');
            } else {
              tile.classList.remove('video-carousel__tile--center');
            }
          });
        }
      }
      this.#rafId = null;
    });

    clearTimeout(this.#scrollTimeout);
    this.#scrollTimeout = setTimeout(() => {
      if (!this.#isMobile()) {
        this.#updateCenterVideo();
      }
    }, 100);
  }

  #handleMouseDown(e) {
    if (this.#isMobile()) return;
    if (window.matchMedia('(pointer: fine)').matches) {
      this.#dragStartX = e.clientX;
      this.#dragStartY = e.clientY;
      this.#isDragging = false;
    }
  }

  #handleMouseMove(e) {
    if (this.#isMobile()) return;
    if (this.#dragStartX !== null && window.matchMedia('(pointer: fine)').matches) {
      const deltaX = Math.abs(e.clientX - this.#dragStartX);
      const deltaY = Math.abs(e.clientY - this.#dragStartY);

      if (deltaX > 10 && deltaX > deltaY) {
        this.#isDragging = true;
        e.preventDefault();
        this.#carousel.style.scrollBehavior = 'auto';
        this.#carousel.style.scrollSnapType = 'none';
      }
    }
  }

  #handleMouseUp(e) {
    if (this.#isMobile()) return;
    if (this.#dragStartX !== null && window.matchMedia('(pointer: fine)').matches) {
      const deltaX = e.clientX - this.#dragStartX;
      const absDeltaX = Math.abs(deltaX);

      if (this.#isDragging && absDeltaX >= this.#dragThreshold) {
        e.preventDefault();
        e.stopPropagation();

        const currentCenterIndex = this.#getCurrentCenterIndex();
        let targetIndex;

        if (deltaX > 0) {
          targetIndex = currentCenterIndex - 1;
        } else {
          targetIndex = currentCenterIndex + 1;
        }

        if (targetIndex >= 0 && targetIndex < this.#blocksCount) {
          this.#carousel.style.scrollBehavior = 'smooth';
          this.#carousel.style.scrollSnapType = 'x mandatory';
          this.#navigateToIndex(targetIndex);
        }
      }

      this.#dragStartX = null;
      this.#dragStartY = null;
      this.#isDragging = false;
      this.#carousel.style.scrollBehavior = '';
      this.#carousel.style.scrollSnapType = '';
    }
  }

  #handleMouseLeave() {
    if (this.#isMobile()) return;
    this.#dragStartX = null;
    this.#dragStartY = null;
    this.#isDragging = false;
    this.#carousel.style.scrollBehavior = '';
    this.#carousel.style.scrollSnapType = '';
  }

  #handleTileClick(tile, originalIndex) {
    const centerTile = this.#findCenterTile();
    if (!centerTile) return;

    const currentCenterIndex = this.#getOriginalIndex(centerTile);
    const clickedRect = tile.getBoundingClientRect();
    const centerRect = centerTile.getBoundingClientRect();

    const clickedCenterX = clickedRect.left + clickedRect.width / 2;
    const centerCenterX = centerRect.left + centerRect.width / 2;

    let targetIndex;
    if (clickedCenterX < centerCenterX) {
      targetIndex = currentCenterIndex - 1;
    } else if (clickedCenterX > centerCenterX) {
      targetIndex = currentCenterIndex + 1;
    } else {
      return;
    }

    if (targetIndex >= 0 && targetIndex < this.#blocksCount) {
      this.#navigateToIndex(targetIndex);
    }
  }

  #initializeCarousel() {
    if (this.#carousel.clientWidth === 0) {
      setTimeout(() => this.#initializeCarousel(), 50);
      return;
    }

    if (this.#isMobile()) {
      this.#tiles.forEach((tile) => {
        tile.classList.remove('video-carousel__tile--center');
        const videoMedia = tile.querySelector('video-media');
        if (videoMedia) {
          videoMedia.removeAttribute('autoplay');
        }
      });
      this.#pauseAllVideos({ resetFacade: true });
      this.#carousel.scrollTo({ left: 0, behavior: 'auto' });
      this.#updateProgress();
      return;
    }

    const startPos = this.#calculateStartPosition();
    this.#carousel.scrollTo({
      left: Math.max(0, startPos),
      behavior: 'auto'
    });

    setTimeout(() => {
      this.#updateCenterVideo();
    }, 100);
  }
}

if (!customElements.get('video-carousel')) {
  customElements.define('video-carousel', VideoCarousel);
}
